import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { ColDef } from 'ag-grid-community';
import { AxiosResponse } from 'axios';
import { CreateOfferedReportDTO, OfferedReport, OfferedReportHistoryEntry, DATE_FORMATS } from '@callmart/types';
import type { CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';
import { useTranslation } from 'react-i18next';
import { useOfferedClusters } from '../hooks/api/offeredClusters';
import { useLatestOfferedReports, useOfferedReportsExportData } from '../hooks/api/offeredReports';
import {
    createTableRows,
    getSlotTimeColumnDef,
    parseCellValue,
    percentageCellStyleRenderer,
} from '../utils/agGridUtils';
import { integerComparator } from '../components/AgGrid/comparators';
import { integerValueFormatter } from '../utils/numberUtils';
import { getAllDatesInAMonth, getAllHourSlotsInATimeRange, getSlotDateTime } from '../utils/dateUtils';
import { createOfferedReports } from '../services/offeredReport';
import { useAlertMessage } from '../hooks';
import ExportToExcelButton from '../components/Buttons/ExportToExcelButton';
import TableGridItem from '../components/UI/TableGridItem';
import GridContainer from '../components/Layout/GridContainer';
import { useExpireLocalStorage } from '../hooks/useExpireLocalStorage';
import { useDebounce } from '../hooks/useDebounce';
import { EditedCellRenderer } from '../components/OfferedReport/EditedCellRenderer';
import { useCellPopover } from '../hooks/useCellPopover';
import CardPopover from '../components/UI/CardPopover';
import OfferedReportHistory from '../components/OfferedReport/OfferedReportHistory';
import { useHEMTUsers } from '../hooks/api/useHEMTUsers';
import { handleDatePickerMonthChange } from '../utils/componentUtils';
import CustomDatePicker from '../components/UI/CustomDatePicker';
import { OfferedClusterSelect } from '../components/Selects/OfferedClusterSelect';
import Container from '../components/Layout/Container';
import dayjs, { Dayjs } from '@utils/dayjs';

const exportColumnNames = ['Worktype', 'Kanal', 'Task', 'Uhrzeit', 'Datum', 'Value'];

interface DayCell {
    date: string;
    slotId?: string;
    value?: number | null;
    hasError?: boolean;
    history?: OfferedReportHistoryEntry[];
    updatedBy?: number;
    createdBy?: number;
    updatedAt?: Date;
    createdAt?: Date;
}

const createMapWithTableRows = (allDatesInAMonth: Dayjs[], offeredReports: OfferedReport[]) => {
    const rowsMap = new Map<string, Record<string, DayCell>>();
    const allHourSlots = getAllHourSlotsInATimeRange({
        startTime: 0,
        endTime: 23,
        displayHoursInHH: true,
        displayMinutesInmm: true,
    });

    for (let i = 0; i < allHourSlots.length; i++) {
        const hourSlot = allHourSlots[i];

        const valuesPerDay: Record<string, DayCell> = {};
        for (const date of allDatesInAMonth) {
            const slotDate = `${date.format(DATE_FORMATS.DATE)}-${hourSlot}`;
            const offeredReport = offeredReports.find((r) => getSlotDateTime(r.slotId) === slotDate);

            valuesPerDay[`day${date.format(DATE_FORMATS.DAY_ONLY)}`] = {
                value: offeredReport?.value,
                date: slotDate,
                slotId: offeredReport?.slotId,
                history: offeredReport?.history,
                updatedBy: offeredReport?.updatedBy || undefined,
                createdBy: offeredReport?.createdBy || undefined,
                updatedAt: offeredReport?.updatedAt,
                createdAt: offeredReport?.createdAt,
            };
        }

        rowsMap.set(hourSlot, valuesPerDay);
    }

    return rowsMap;
};

const createColumnDefs = (
    allDatesInAMonth: Dayjs[],
    maxValue: number,
    onHistoryClick: (cellData: DayCell, event: React.MouseEvent) => void
) => {
    const columns: ColDef<OfferedReport & Record<string, DayCell>>[] = [getSlotTimeColumnDef() as ColDef];

    for (let i = 0; i < allDatesInAMonth.length; i++) {
        const colId = `day${allDatesInAMonth[i].format(DATE_FORMATS.DAY_ONLY)}`;
        columns.push({
            colId: colId,
            // keyCreator: (params) => [params.data.slotTime, params.data[colId].date, params.value].join('-'),
            headerName: `${allDatesInAMonth[i].format(DATE_FORMATS.DAY_MONTH)}`,
            resizable: true,
            comparator: (valueA, valueB) => {
                return integerComparator({ valueA, valueB });
            },
            valueGetter: (params) => params?.data?.[colId]?.value,
            valueFormatter: integerValueFormatter,
            valueSetter: (params) => {
                const parsed = parseCellValue(params.newValue);
                if (parsed === undefined) return false;
                // Round to integer — offeredReport.value is an INTEGER column
                params.data[colId].value = typeof parsed === 'number' ? Math.round(parsed) : parsed;
                return true;
            },
            editable: true,
            cellRenderer: EditedCellRenderer,
            cellRendererParams: {
                onHistoryClick,
            },
            cellStyle: ({ value, data }) =>
                data?.[colId]?.hasError
                    ? { backgroundColor: 'var(--error)', color: 'var(--white)' }
                    : percentageCellStyleRenderer({
                          value,
                          maxValue,
                      }),
            minWidth: 90,
        });
    }

    return columns;
};

const VolumeOffered = () => {
    const { t } = useTranslation();
    const gridRef = useRef(null);
    const { dispatchAlert } = useAlertMessage();
    const [selectedOfferedCluster, setSelectedOfferedCluster] = useExpireLocalStorage<number>(
        'volume-offered-selected-cluster',
        0
    );
    const [changedRows, setChangedRows] = useState<CreateOfferedReportDTO[]>([]);
    const debouncedChangedRows = useDebounce(changedRows, 10);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const { data: users } = useHEMTUsers(true);
    const { hoveredCell, hoveredCellHistoryList, popoverOpen, popoverClose } = useCellPopover();

    const { data: offeredClusters, isLoading } = useOfferedClusters();

    const { data: offeredReports, refetch: refetchOfferedReports } = useLatestOfferedReports({
        date: selectedDate,
        offeredCluster: selectedOfferedCluster,
    });

    useEffect(() => {
        if (!selectedOfferedCluster && offeredClusters.length) {
            setSelectedOfferedCluster(offeredClusters[0].id);
        }
    }, [selectedOfferedCluster, offeredClusters, setSelectedOfferedCluster]);

    const maxTotalValue = useMemo(() => {
        return offeredReports.length ? Math.max(...(offeredReports || []).map(({ value }) => value)) : 0;
    }, [offeredReports]);

    const handleHistoryClick = useCallback(
        (cellData: DayCell, event: React.MouseEvent) => {
            popoverOpen(true, event.target as HTMLElement, cellData);
        },
        [popoverOpen]
    );

    const allDatesInAMonth = useMemo((): Dayjs[] => getAllDatesInAMonth(selectedDate), [selectedDate]);
    const columnDefs = useMemo(
        () => createColumnDefs(allDatesInAMonth, maxTotalValue, handleHistoryClick),
        [allDatesInAMonth, maxTotalValue, handleHistoryClick]
    );
    const tableRows = useMemo(
        () =>
            createTableRows(createMapWithTableRows(allDatesInAMonth, offeredReports), 'slotTime').map((row) => {
                return {
                    id: [selectedOfferedCluster, row.slotTime].join('-'),
                    ...row,
                };
            }),
        [allDatesInAMonth, offeredReports, selectedOfferedCluster]
    );

    const { mutateAsync: createMultipleOfferedReports } = useMutation({ mutationFn: createOfferedReports });

    const { refetch: refetchExportData } = useOfferedReportsExportData({
        date: selectedDate,
        fetchImmediately: false,
    });

    useEffect(() => {
        if (debouncedChangedRows.length) {
            createMultipleOfferedReports(debouncedChangedRows)
                .then(() => {
                    setChangedRows((prev) => prev.filter((row) => !debouncedChangedRows.includes(row)));
                })
                .catch((error) => {
                    dispatchAlert({
                        message: t(error.message || 'error.offeredReportsNotSaved'),
                        type: 'error',
                    });
                })
                .then(() => refetchOfferedReports());
        }
    }, [createMultipleOfferedReports, debouncedChangedRows, dispatchAlert, refetchOfferedReports, t]);

    const handleCellValueChange = (params: CellValueChangedEvent) => {
        const colId = params.column.getColId();
        const value = parseCellValue(params.value);
        if (params.data[colId]) {
            // Reject ONLY undefined (invalid input)
            // Allow null (deletion) and numbers (update)
            if (value === undefined) {
                params.data[colId].hasError = true;
                dispatchAlert({
                    message: t('validation.validNumber'),
                    type: 'error',
                });
                return;
            }

            // Clear error state for valid inputs
            params.data[colId].hasError = false;

            setChangedRows((prev) =>
                prev.concat({
                    // Round to integer — offeredReport.value is an INTEGER column
                    value: typeof value === 'number' ? Math.round(value) : value,
                    offeredClusterId: selectedOfferedCluster,
                    date: params.data[colId].date,
                })
            );
        }
    };

    const getExportFileName = () => {
        return `Offered_Report_${dayjs(selectedDate).format(DATE_FORMATS.MONTH_YEAR)}.xlsx`;
    };

    const getExportData = (offeredReports: AxiosResponse<OfferedReport[]>) => {
        return offeredReports.data;
    };

    return (
        <Container>
            <GridContainer>
                <Grid item xs={12} md={5} lg={4} xl={3}>
                    <OfferedClusterSelect
                        selectedCluster={selectedOfferedCluster}
                        onChange={setSelectedOfferedCluster}
                        offeredClusterOptions={offeredClusters}
                    />
                </Grid>
                <Grid item xs={12} md={4} lg={2}>
                    <CustomDatePicker
                        date={selectedDate}
                        views={['year', 'month']}
                        openTo="month"
                        onChange={(newValue: Dayjs) =>
                            handleDatePickerMonthChange(selectedDate, newValue, setSelectedDate)
                        }
                    />
                </Grid>
                <Grid item xs={3} md={1} lg={1} xl={1}>
                    <ExportToExcelButton
                        columnNames={exportColumnNames}
                        sheetName="Tabelle1"
                        getFileName={getExportFileName}
                        wbTitle="Offered_Report"
                        wbSubject="Export Offered Report"
                        getDataFromDb={refetchExportData}
                        getExportData={getExportData}
                        getColumnNames={undefined}
                        createRowFromExportData={undefined}
                        exportData={undefined}
                    />
                </Grid>
                <TableGridItem
                    ref={gridRef}
                    rowData={tableRows}
                    defaultColDef={{
                        editable: true,
                        filter: true,
                        sortable: true,
                    }}
                    columnDefs={columnDefs}
                    onCellValueChanged={handleCellValueChange}
                    suppressScrollOnNewData={true}
                    cellSelection={true}
                    loading={isLoading}
                />
            </GridContainer>
            <CardPopover anchorEl={hoveredCell} onClose={popoverClose}>
                {hoveredCellHistoryList && (
                    <OfferedReportHistory
                        history={hoveredCellHistoryList.history || []}
                        users={users}
                        currentValue={hoveredCellHistoryList.value || 0}
                        currentUserId={hoveredCellHistoryList.updatedBy || hoveredCellHistoryList.createdBy}
                        currentTimestamp={hoveredCellHistoryList.updatedAt || hoveredCellHistoryList.createdAt}
                    />
                )}
            </CardPopover>
        </Container>
    );
};

export default VolumeOffered;
